import getRouterBasename from '@/lib/router';
import AppWrapper from 'AppWrapper';
import React from 'react';
import ReactDOM from 'react-dom/client';

// Import Organizations page for standalone rendering
import Organizations from '@/pages/Organizations';

import { ThemeProvider } from '@/components/ThemeProvider';

import './index.css';

import { i18nSetupLocalization } from './i18n';

i18nSetupLocalization();

// Check if we're on organizations page BEFORE any other imports
const basename = getRouterBasename();
const currentPath = window.location.pathname;
const expectedPath = basename + '/organizations';
const isOrganizationsPage =
    currentPath === expectedPath || currentPath === '/organizations';

// @ts-ignore
window.theme = {
    light: {
        '--font-sans': "'Montserrat', sans-serif",
        '--font-mono':
            "source-code-pro, Menlo, Monaco, Consolas, 'Courier New', monospace",
        '--background': '0deg 0% 99.5%',
        '--foreground': '207.6 64.9% 11.2%',
        '--card': '225 50% 98.4%',
        '--card-foreground': '207.6 64.9% 11.2%',
        '--popover': '225 50% 98.4%',
        '--popover-foreground': '207.6 64.9% 11.2%',
        '--primary': '206 55.6% 14.1%',
        '--primary-foreground': '225 50% 98.4%',
        '--secondary': '211.8 11.9% 90%',
        '--secondary-foreground': '207.6 64.9% 11.2%',
        '--muted': '211.8 11.9% 95%',
        '--muted-foreground': '208deg 9.2% 46.6%',
        '--accent': '211.8 11.9% 95%',
        '--accent-foreground': '207.6 64.9% 11.2%',
        '--destructive': '0 84.2% 60.2%',
        '--destructive-foreground': '225 50% 98.4%',
        '--border': '211.8 11.9% 90%',
        '--input': '211.8 11.9% 90%',
        '--ring': '206 55.6% 14.1%',
        '--radius': '0.75rem',
        '--sidebar-background': '0 0% 100%',
        '--sidebar-foreground': '0deg 0% 5.1%',
        '--sidebar-primary': '206 55.6% 14.1%',
        '--sidebar-primary-foreground': '225 50% 98.4%',
        '--sidebar-accent': '0deg 0% 96.08%',
        '--sidebar-accent-foreground': '207.6 64.9% 11.2%',
        '--sidebar-border': '211.8 11.9% 90%',
        '--sidebar-ring': '206 55.6% 14.1%',
        // custom
        '--scrollbar-color': '#0000001a',
        '--scrollbar-color-hover': '#0003'
    },
    dark: {
        '--font-sans': "'Montserrat', sans-serif",
        '--font-mono':
            "source-code-pro, Menlo, Monaco, Consolas, 'Courier New', monospace",
        '--background': '0 0.0% 12.9%',
        '--foreground': '0 0.0% 100.0%',
        '--card': '0 0.0% 18.8%',
        '--card-foreground': '0 0.0% 100.0%',
        '--popover': '0 0.0% 18.8%',
        '--popover-foreground': '0 0.0% 100.0%',
        '--primary': '0 0.0% 9.4%',
        '--primary-foreground': '0 0.0% 100.0%',
        '--secondary': '0 0.0% 18.8%',
        '--secondary-foreground': '0 0.0% 95.3%',
        '--muted': '0 0.0% 25.5%',
        '--muted-foreground': '0 0.0% 62.6%',
        '--accent': '0 0.0% 25.5%',
        '--accent-foreground': '0 0.0% 100.0%',
        '--destructive': '1.3 74.6% 52.2%',
        '--destructive-foreground': '0 0.0% 100.0%',
        '--border': '0 0.0% 21.6%',
        '--input': '0 0.0% 21.6%',
        '--ring': '209 100.0% 23.9%',
        '--radius': '0.75rem',
        '--sidebar-background': '0 0.0% 9.4%',
        '--sidebar-foreground': '0 0.0% 100.0%',
        '--sidebar-primary': '209.1 98.1% 20.2%',
        '--sidebar-primary-foreground': '0 0.0% 100.0%',
        '--sidebar-accent': '0 0.0% 18.6%',
        '--sidebar-accent-foreground': '0 0.0% 100.0%',
        '--sidebar-border': '0 0.0% 18.5%',
        '--sidebar-ring': '209 100.0% 23.9%',
        // custom
        '--scrollbar-color': '#ffffff1a', // was #ffffff1a
        '--scrollbar-color-hover': '#fff3' // was #fff3
    }
};

// Render organizations page standalone without any Chainlit contexts
if (isOrganizationsPage) {
    // Import BrowserRouter for navigation support
    import('react-router-dom').then(({ BrowserRouter }) => {
        ReactDOM.createRoot(
            document.getElementById('root') as HTMLElement
        ).render(
            <React.StrictMode>
                <BrowserRouter basename={getRouterBasename()}>
                    <ThemeProvider>
                        <Organizations />
                    </ThemeProvider>
                </BrowserRouter>
            </React.StrictMode>
        );
    });
} else {
    // Dynamically import Chainlit dependencies only for non-organizations pages
    import('api').then(({ apiClient }) => {
        import('@chainlit/react-client').then(({ ChainlitContext }) => {
            import('recoil').then(({ RecoilRoot }) => {
                ReactDOM.createRoot(
                    document.getElementById('root') as HTMLElement
                ).render(
                    <React.StrictMode>
                        <ChainlitContext.Provider value={apiClient}>
                            <RecoilRoot>
                                <AppWrapper />
                            </RecoilRoot>
                        </ChainlitContext.Provider>
                    </React.StrictMode>
                );
            });
        });
    });
}
